import { type SupportedLanguage } from './languageSettings';
import { en } from '../translations/en';
import { fr } from '../translations/fr';
import { de } from '../translations/de';
import { pt } from '../translations/pt';
import { es } from '../translations/es';

export type TranslationKey =
  | 'coCarer.removedNotice.title'
  | 'coCarer.removedNotice.body'
  | 'coCarer.removedNotice.ok'
  | 'nav.records'
  | 'nav.meds'
  | 'nav.guide'
  | 'nav.options'
  | 'topbar.welcome'
  | 'splash.tagline'
  | 'records.tab.todayEntry'
  | 'records.tab.allCards'
  | 'records.tab.worksheet'
  | 'meds.title'
  | 'meds.empty'
  | 'trends.title'
  | 'trends.noDataTitle'
  | 'trends.noDataBody'
  | 'trends.moreDataNeeded'
  | 'trends.moreDataNeededBody'
  | 'trends.avgWeight'
  | 'trends.avgHeartRate'
  | 'trends.avgRespRate'
  | 'trends.avgMucousMembrane'
  | 'trends.avgWaterIntake'
  | 'trends.avgTemperature'
  | 'trends.weightTrend'
  | 'trends.heartRateTrend'
  | 'trends.respRateTrend'
  | 'trends.mucousMembraneTrend'
  | 'trends.fluidIntakeTrend'
  | 'trends.foodIntakeTrend'
  | 'trends.avgFoodIntake'
  | 'trends.day'
  | 'trends.days'
  | 'trends.tooltipWeight'
  | 'trends.tooltipFluid'
  | 'trends.tooltipFood'
  | 'trends.tooltipHeartRate'
  | 'trends.tooltipRespRate'
  | 'trends.tooltipMucousMembrane'
  | 'trends.fluidBalanceTrend'
  | 'trends.tempTrend'
  | 'trends.moodTrend'
  | 'trends.mucousMembrane.pink'
  | 'trends.mucousMembrane.red'
  | 'trends.mucousMembrane.blue'
  | 'trends.mucousMembrane.white'
  | 'trends.noMucousMembraneData'
  | 'trends.moodLabel.terrible'
  | 'trends.moodLabel.notGreat'
  | 'trends.moodLabel.okay'
  | 'trends.moodLabel.good'
  | 'trends.moodLabel.great'
  | 'trends.tooltipFluidBalance'
  | 'trends.tooltipTemp'
  | 'trends.tooltipMood'
  | 'trends.customise'
  | 'trends.resetLayout'
  | 'trends.viewPlans'
  | 'trends.trendsLockedTitle'
  | 'trends.trendsLockedBody'
  | 'trends.lifetimeLockedTitle'
  | 'trends.lifetimeLockedBody'
  | 'guide.digestLockedTitle'
  | 'guide.digestLockedBody'
  | 'options.title'
  | 'options.appPreferences'
  | 'options.language'
  | 'options.units'
  | 'options.metric'
  | 'options.imperial'
  | 'options.dateFormat'
  | 'options.timeFormat'
  | 'options.transplant'
  | 'options.editProfile'
  | 'options.saveToHomeScreen'
  | 'options.logOut'
  | 'options.fullName'
  | 'options.profession'
  | 'options.professionPlaceholder'
  | 'options.emailAddress'
  | 'options.emailCannotChange'
  | 'options.transplantDate'
  | 'options.changePassword'
  | 'options.deleteAccount'
  | 'options.cancel'
  | 'options.saveChanges'
  | 'login.email'
  | 'login.password'
  | 'login.forgotPassword'
  | 'login.signIn'
  | 'login.continueWithGoogle'
  | 'login.noAccount'
  | 'login.register'
  | 'login.or'
  | 'login.rememberMe'
  | 'forgot.title'
  | 'forgot.subtitle'
  | 'forgot.checkInboxTitle'
  | 'forgot.checkInboxBody'
  | 'forgot.spamHint'
  | 'forgot.backToSignIn'
  | 'forgot.sendResetLink'
  | 'forgot.rememberPassword'
  | 'forgot.legal'
  | 'forgot.errorNoAccount'
  | 'forgot.errorNetwork'
  | 'forgot.errorGeneric'
  | 'verify.title'
  | 'verify.subtitle'
  | 'verify.errorResend'
  | 'verify.resent'
  | 'verify.emailSent'
  | 'verify.resend'
  | 'verify.backToSignIn'
  | 'verify.spamHint'
  | 'authAction.loading'
  | 'authAction.error.missingInfo'
  | 'authAction.error.unsupportedMode'
  | 'authAction.error.expired'
  | 'authAction.error.invalid'
  | 'authAction.error.userDisabled'
  | 'authAction.error.userNotFound'
  | 'authAction.error.weakPassword'
  | 'authAction.error.generic'
  | 'authAction.error.resetInvalid'
  | 'authAction.error.passwordMin'
  | 'authAction.error.passwordMismatch'
  | 'authAction.verifySuccessTitle'
  | 'authAction.verifySuccessBody'
  | 'authAction.recoverSuccessTitle'
  | 'authAction.recoverSuccessBody'
  | 'authAction.continue'
  | 'authAction.goToSignIn'
  | 'authAction.resetTitle'
  | 'authAction.resetBody'
  | 'authAction.newPassword'
  | 'authAction.confirmNewPassword'
  | 'authAction.updatePassword'
  | 'authAction.resetSuccessTitle'
  | 'authAction.resetSuccessBody'
  | 'authAction.signIn'
  | 'authAction.linkUnavailableTitle'
  | 'authAction.requestNewResetLink'
  | 'authAction.backToSignIn'
  | 'register.title'
  | 'register.fullName'
  | 'register.operationDate'
  | 'register.email'
  | 'register.password'
  | 'register.confirmPassword'
  | 'register.createAccount'
  | 'register.haveAccount'
  | 'register.signIn'
  | 'guide.comingSoon'
  | 'guide.motivation'
  // Lab Glossary
  | 'guide.labGlossary.intro'
  | 'guide.labGlossary.search'
  | 'guide.labGlossary.all'
  | 'guide.labGlossary.noMarkersFound'
  | 'guide.labGlossary.referenceRange'
  | 'guide.labGlossary.note'
  // Markers - Kidney Function
  | 'guide.labGlossary.marker.creatinine.name'
  | 'guide.labGlossary.marker.creatinine.abbr'
  | 'guide.labGlossary.marker.creatinine.category'
  | 'guide.labGlossary.marker.creatinine.why'
  | 'guide.labGlossary.marker.creatinine.typical'
  | 'guide.labGlossary.marker.creatinine.note'
  | 'guide.labGlossary.marker.egfr.name'
  | 'guide.labGlossary.marker.egfr.abbr'
  | 'guide.labGlossary.marker.egfr.category'
  | 'guide.labGlossary.marker.egfr.why'
  | 'guide.labGlossary.marker.egfr.typical'
  | 'guide.labGlossary.marker.egfr.note'
  | 'guide.labGlossary.marker.bun.name'
  | 'guide.labGlossary.marker.bun.abbr'
  | 'guide.labGlossary.marker.bun.category'
  | 'guide.labGlossary.marker.bun.why'
  | 'guide.labGlossary.marker.bun.typical'
  // Markers - Immunosuppressants
  | 'guide.labGlossary.marker.tacrolimus.name'
  | 'guide.labGlossary.marker.tacrolimus.abbr'
  | 'guide.labGlossary.marker.tacrolimus.category'
  | 'guide.labGlossary.marker.tacrolimus.why'
  | 'guide.labGlossary.marker.tacrolimus.typical'
  | 'guide.labGlossary.marker.tacrolimus.note'
  | 'guide.labGlossary.marker.ciclosporin.name'
  | 'guide.labGlossary.marker.ciclosporin.abbr'
  | 'guide.labGlossary.marker.ciclosporin.category'
  | 'guide.labGlossary.marker.ciclosporin.why'
  | 'guide.labGlossary.marker.ciclosporin.typical'
  | 'guide.labGlossary.marker.mmf.name'
  | 'guide.labGlossary.marker.mmf.abbr'
  | 'guide.labGlossary.marker.mmf.category'
  | 'guide.labGlossary.marker.mmf.why'
  | 'guide.labGlossary.marker.mmf.typical'
  // Markers - Electrolytes
  | 'guide.labGlossary.marker.potassium.name'
  | 'guide.labGlossary.marker.potassium.abbr'
  | 'guide.labGlossary.marker.potassium.category'
  | 'guide.labGlossary.marker.potassium.why'
  | 'guide.labGlossary.marker.potassium.typical'
  | 'guide.labGlossary.marker.potassium.note'
  | 'guide.labGlossary.marker.sodium.name'
  | 'guide.labGlossary.marker.sodium.abbr'
  | 'guide.labGlossary.marker.sodium.category'
  | 'guide.labGlossary.marker.sodium.why'
  | 'guide.labGlossary.marker.sodium.typical'
  | 'guide.labGlossary.marker.bicarbonate.name'
  | 'guide.labGlossary.marker.bicarbonate.abbr'
  | 'guide.labGlossary.marker.bicarbonate.category'
  | 'guide.labGlossary.marker.bicarbonate.why'
  | 'guide.labGlossary.marker.bicarbonate.typical'
  | 'guide.labGlossary.marker.phosphate.name'
  | 'guide.labGlossary.marker.phosphate.abbr'
  | 'guide.labGlossary.marker.phosphate.category'
  | 'guide.labGlossary.marker.phosphate.why'
  | 'guide.labGlossary.marker.phosphate.typical'
  | 'guide.labGlossary.marker.calcium.name'
  | 'guide.labGlossary.marker.calcium.abbr'
  | 'guide.labGlossary.marker.calcium.category'
  | 'guide.labGlossary.marker.calcium.why'
  | 'guide.labGlossary.marker.calcium.typical'
  // Markers - Blood Count
  | 'guide.labGlossary.marker.haemoglobin.name'
  | 'guide.labGlossary.marker.haemoglobin.abbr'
  | 'guide.labGlossary.marker.haemoglobin.category'
  | 'guide.labGlossary.marker.haemoglobin.why'
  | 'guide.labGlossary.marker.haemoglobin.typical'
  | 'guide.labGlossary.marker.wbc.name'
  | 'guide.labGlossary.marker.wbc.abbr'
  | 'guide.labGlossary.marker.wbc.category'
  | 'guide.labGlossary.marker.wbc.why'
  | 'guide.labGlossary.marker.wbc.typical'
  | 'guide.labGlossary.marker.wbc.note'
  | 'guide.labGlossary.marker.platelets.name'
  | 'guide.labGlossary.marker.platelets.abbr'
  | 'guide.labGlossary.marker.platelets.category'
  | 'guide.labGlossary.marker.platelets.why'
  | 'guide.labGlossary.marker.platelets.typical'
  // Markers - Liver Function
  | 'guide.labGlossary.marker.altAst.name'
  | 'guide.labGlossary.marker.altAst.abbr'
  | 'guide.labGlossary.marker.altAst.category'
  | 'guide.labGlossary.marker.altAst.why'
  | 'guide.labGlossary.marker.altAst.typical'
  // Markers - Metabolic
  | 'guide.labGlossary.marker.uricAcid.name'
  | 'guide.labGlossary.marker.uricAcid.abbr'
  | 'guide.labGlossary.marker.uricAcid.category'
  | 'guide.labGlossary.marker.uricAcid.why'
  | 'guide.labGlossary.marker.uricAcid.typical'
  | 'guide.labGlossary.marker.hba1c.name'
  | 'guide.labGlossary.marker.hba1c.abbr'
  | 'guide.labGlossary.marker.hba1c.category'
  | 'guide.labGlossary.marker.hba1c.why'
  | 'guide.labGlossary.marker.hba1c.typical'
  // Markers - Infections
  | 'guide.labGlossary.marker.cmv.name'
  | 'guide.labGlossary.marker.cmv.abbr'
  | 'guide.labGlossary.marker.cmv.category'
  | 'guide.labGlossary.marker.cmv.why'
  | 'guide.labGlossary.marker.cmv.typical'
  | 'guide.labGlossary.marker.cmv.note'
  | 'guide.labGlossary.marker.bkv.name'
  | 'guide.labGlossary.marker.bkv.abbr'
  | 'guide.labGlossary.marker.bkv.category'
  | 'guide.labGlossary.marker.bkv.why'
  | 'guide.labGlossary.marker.bkv.typical'
  // Markers - Kidney Function (additional)
  | 'guide.labGlossary.marker.upcr.name'
  | 'guide.labGlossary.marker.upcr.abbr'
  | 'guide.labGlossary.marker.upcr.category'
  | 'guide.labGlossary.marker.upcr.why'
  | 'guide.labGlossary.marker.upcr.typical'
  | 'guide.labGlossary.marker.c0d.name'
  | 'guide.labGlossary.marker.c0d.abbr'
  | 'guide.labGlossary.marker.c0d.category'
  | 'guide.labGlossary.marker.c0d.why'
  | 'guide.labGlossary.marker.c0d.typical'
  | 'guide.labGlossary.category.kidneyFunction'
  | 'guide.labGlossary.category.immunosuppressants'
  | 'guide.labGlossary.category.electrolytes'
  | 'guide.labGlossary.category.bloodCount'
  | 'guide.labGlossary.category.liverFunction'
  | 'guide.labGlossary.category.metabolic'
  | 'guide.labGlossary.category.infections'
  // Records page
  | 'records.todaysOverview'
  | 'records.selectedDayOverview'
  | 'records.weight'
  | 'records.fluids'
  | 'records.fluidIntake'
  | 'records.foodIntake'
  | 'records.urineOutput'
  | 'records.saveWeight'
  | 'records.undoWeight'
  | 'records.weightRecorded'
  | 'records.weightRecordedToday'
  | 'records.add'
  | 'records.total'
  | 'records.morningVitals'
  | 'records.afternoonVitals'
  | 'records.morningVitalsRecorded'
  | 'records.afternoonVitalsRecorded'
  | 'records.saveVitals'
  | 'records.resetTodaysData'
  | 'records.resetConfirmMessage'
  | 'records.dailyLimitReached'
  | 'records.newEntry'
  | 'records.addNewEntry'
  | 'records.saveEntry'
  | 'records.cancel'
  | 'records.date'
  | 'records.morning'
  | 'records.afternoon'
  | 'records.daily'
  | 'records.bpSystolic'
  | 'records.bpDiastolic'
  | 'records.pulse'
  | 'records.temp'
  | 'records.downloadCsvFor'
  | 'records.thisWeek'
  | 'records.thisMonth'
  | 'records.thisYear'
  | 'records.shareDataTitle'
  | 'records.dataCopiedToClipboard'
  | 'records.noLogEntriesYet'
  | 'records.fillAtLeastOneField'
  | 'records.fluid'
  | 'records.amBp'
  | 'records.amPulse'
  | 'records.amTemp'
  | 'records.pmBp'
  | 'records.pmPulse'
  | 'records.pmTemp'
  | 'records.vitals'
  | 'records.vitalsTime'
  | 'records.filterBy'
  | 'records.week'
  | 'records.month'
  | 'records.year'
  | 'records.entry'
  | 'records.entries'
  | 'records.today'
  | 'records.fluidIn'
  | 'records.deleteLogEntry'
  | 'records.undoWeightEntry'
  | 'records.undoLastFluidEntry'
  | 'records.undoLastUrineEntry'
  | 'records.toolbar.addNote'
  | 'records.toolbar.editNote'
  | 'records.toolbar.intake'
  | 'records.toolbar.saveNote'
  | 'records.toolbar.updateNote'
  | 'records.toolbar.notesPlaceholder'
  | 'records.toolbar.add'
  | 'records.toolbar.save'
  | 'records.undoMorningVitals'
  | 'records.undoAfternoonVitals'
  | 'records.amount'
  | 'records.tab.documents'
  | 'records.documentsTitle'
  | 'records.documentsSubtitle'
  | 'records.searchDocuments'
  | 'records.uploadDocument'
  | 'records.takePhoto'
  | 'records.maxFilesReached'
  | 'records.categoryAll'
  | 'records.bucketLabs'
  | 'records.bucketScans'
  | 'records.bucketLetters'
  | 'records.bucketDischarge'
  | 'records.bucketPrescriptions'
  | 'records.bucketOther'
  | 'records.noDocumentsYet'
  | 'records.titleOptional'
  | 'records.noteOptional'
  | 'records.documentDate'
  | 'records.uploading'
  | 'records.openFile'
  | 'records.deleteFile'
  | 'records.deleteFileConfirm'
  // Notification Center
  | 'notifications.title'
  | 'notifications.allReminders'
  | 'notifications.storageNote'
  | 'notifications.new'
  | 'notifications.markAllRead'
  | 'notifications.allCaughtUp'
  | 'notifications.clearAll'
  | 'notifications.clearAllConfirm'
  // Delete Confirm Modal
  | 'deleteConfirm.defaultTitle'
  | 'deleteConfirm.cancel'
  | 'deleteConfirm.delete'
  | 'deleteConfirm.dontShowAgain'
  // Settings Panel
  | 'settings.title'
  | 'settings.preferences'
  | 'settings.language'
  | 'settings.units'
  | 'settings.metric'
  | 'settings.imperial'
  | 'settings.dateFormat'
  | 'settings.timeFormat'
  | 'settings.logOut'
  | 'settings.editProfile'
  | 'settings.fullName'
  | 'settings.profession'
  | 'settings.professionPlaceholder'
  | 'settings.emailAddress'
  | 'settings.emailCannotChange'
  | 'settings.transplantDate'
  | 'settings.changePassword'
  | 'settings.deleteAccount'
  | 'settings.deleteAccountConfirm'
  | 'settings.cancel'
  | 'deleteAccount.title'
  | 'deleteAccount.bullet1'
  | 'deleteAccount.bullet2'
  | 'deleteAccount.bullet3'
  | 'deleteAccount.footer'
  | 'deleteAccount.confirm'
  | 'deleteAccount.cancel'
  | 'settings.saveChanges'
  // Meds page
  | 'meds.removeMedication'
  | 'meds.removeConfirm'
  | 'meds.medCount'
  | 'meds.medsCount'
  | 'meds.remindersEnabled'
  | 'meds.remindersDisabled'
  | 'meds.addMedication'
  | 'meds.cancel'
  | 'meds.medicationName'
  | 'meds.medicationNamePlaceholder'
  | 'meds.dosage'
  | 'meds.dosagePlaceholder'
  | 'meds.timeslots'
  | 'meds.addTime'
  | 'meds.type'
  | 'meds.shapeCapsule'
  | 'meds.shapeOval'
  | 'meds.shapeTwoPart'
  | 'meds.shapeDiamond'
  | 'meds.shapeRound'
  | 'meds.shapeFlat'
  | 'meds.shapeTriangle'
  | 'meds.pillColour'
  | 'meds.customColour'
  | 'meds.fillAllFields'
  | 'meds.edit'
  | 'meds.delete'
  | 'meds.duplicate'
  | 'meds.dropHere'
  | 'meds.editMedication'
  | 'meds.saveChanges'
  // Contacts
  | 'contacts.edit'
  | 'contacts.delete'
  | 'contacts.editContact'
  | 'contacts.addContact'
  | 'contacts.cancel'
  | 'contacts.saveChanges'
  | 'contacts.saveContact'
  | 'contacts.name'
  | 'contacts.phoneNumber'
  | 'contacts.email'
  | 'contacts.nephrologist'
  | 'contacts.pharmacy'
  | 'contacts.emergency'
  | 'contacts.other'
  | 'contacts.nameRequired'
  | 'contacts.phoneRequired'
  | 'contacts.deleteContact'
  | 'contacts.deleteContactMsg'
  | 'contacts.addNewContact'
  | 'contacts.noContacts'
  | 'contacts.addCareTeam'
  | 'contacts.sortRecent'
  | 'contacts.sortAlpha'
  // Notifications settings
  | 'options.notifications'
  | 'options.notificationsAll'
  | 'options.notificationsLogs'
  | 'options.notificationsMeds'
  | 'options.notificationsNone'
  | 'options.subscription'
  | 'options.subscriptionSubtitle'
  | 'options.subscriptionPlan'
  | 'options.subscriptionStatus'
  | 'options.subscriptionStarted'
  | 'options.subscriptionNextPayment'
  | 'options.subscriptionExpires'
  | 'options.subscriptionNoActive'
  | 'options.subscriptionUnavailable'
  | 'account.title'
  | 'account.subtitle'
  | 'account.profile'
  | 'account.profileSubtitle'
  | 'account.profileSaved'
  | 'account.profileSaveError'
  | 'account.savingProfile'
  | 'account.saveProfile'
  | 'account.openDashboard'
  | 'account.starterTrial'
  | 'account.expired'
  | 'account.loading'
  | 'account.trialDaysLeft'
  | 'account.expiredDaysLeft'
  | 'account.currentPlan'
  | 'account.fetchingBilling'
  | 'account.manageSubscription'
  | 'account.security'
  | 'account.memberSince'
  | 'account.lastLogin'
  | 'account.passwordResetSent'
  | 'account.resetPassword'
  | 'account.resetPasswordEmailError'
  | 'account.deleteRequiresRecentLogin'
  | 'account.deleteFailed'
  | 'account.deleting'
  | 'account.dataPrivacy'
  | 'account.exportDescription'
  | 'account.preparingExport'
  | 'account.downloadAllData'
  | 'options.notificationSettings'
  | 'options.remindersSubtitle'  | 'options.permissions'
  | 'options.activate'
  | 'options.logReminderTimes'
  | 'options.logReminderTime'
  | 'options.permissionGrant'
  | 'options.browserBlockedHelp'
  | 'options.appOn'
  | 'options.appOff'
  | 'options.browserAllowed'
  | 'options.browserUnsupported'
  | 'options.browserBlocked'
  | 'options.browserNotAsked'
  | 'options.activityTitle'
  | 'records.dateAlreadyHasEntry'
  | 'records.deleteEntry'
  | 'records.deleteEntryConfirm'
  | 'records.editEntry'
  | 'records.observations'
  | 'records.howAreYouFeeling'
  | 'records.observationsPlaceholder'
  | 'records.saveObservations'
  | 'records.observationsRecorded'
  | 'records.undoObservations'
  | 'records.moodTerribleLabel'
  | 'records.moodBadLabel'
  | 'records.moodOkayLabel'
  | 'records.moodGoodLabel'
  | 'records.moodGreatLabel'
  | 'records.noObservations'
  | 'records.mood'
  | 'records.shareCard'
  | 'records.undoObservation'
  // Register legal
  | 'register.byRegistering'
  | 'register.and'
  | 'register.termsOfService'
  | 'register.privacyPolicy'
  // CSV headers
  | 'records.csv.date'
  | 'records.csv.weight'
  | 'records.csv.fluidIn'
  | 'records.csv.urineOut'
  | 'records.csv.amRegistered'
  | 'records.csv.amBP'
  | 'records.csv.amPulse'
  | 'records.csv.amTemp'
  | 'records.csv.pmRegistered'
  | 'records.csv.pmBP'
  | 'records.csv.pmPulse'
  | 'records.csv.pmTemp'
  | 'records.csv.observations'
  | 'records.csv.healthDiary'
  // Roomies settings
  | 'options.roomies.description'
  | 'options.roomies.nickname'
  | 'options.roomies.nicknameMax'
  | 'options.roomies.nicknamePlaceholder'
  | 'options.roomies.beanColour'
  | 'options.roomies.groupCode'
  | 'options.roomies.groupCodePlaceholder'
  | 'options.roomies.groupCodeHint'
  | 'options.roomies.enableRoomies'
  | 'options.roomies.enableRoomiesHint'
  | 'options.roomies.noChanges'
  | 'options.roomies.saveProfile'
  | 'options.roomies.saved'
  // Companionship (The Koda Fellowship)
  | 'companions.title'
  | 'companions.pageTitle'
  | 'companions.subtitle'
  | 'companions.publicView'
  | 'companions.publicViewHint'
  | 'companions.publicViewOn'
  | 'companions.publicViewOff'
  | 'companions.leaderboardTitle'
  | 'companions.leaderboardEmpty'
  | 'companions.leaderboardEmptyWeek'
  | 'companions.leaderboardHint'
  | 'companions.leaderboardHintWeek'
  | 'companions.tabAll'
  | 'companions.tabWeek'
  | 'companions.you'
  | 'companions.rank'
  | 'companions.score'
  | 'companions.levelShort'
  | 'companions.streakShort'
  | 'companions.weekXPShort'
  | 'companions.hiddenNotice'
  | 'companions.story.title'
  | 'companions.story.modalTitle'
  | 'companions.story.hint'
  | 'companions.story.placeholder'
  | 'companions.story.save'
  | 'companions.story.edit'
  | 'companions.story.clear'
  | 'companions.story.expiresIn'
  | 'companions.story.recovered'
  | 'companions.story.vaccinated'
  | 'companions.story.grooming'
  | 'companions.story.vetVisit'
  | 'companions.story.newTreat'
  | 'companions.story.birthday'
  | 'companions.story.feelingGreat'
  | 'companions.story.restingUp'
  | 'companions.story.newHome'
  // Bean page
  | 'bean.back'
  | 'bean.level'
  | 'bean.yourBean'
  | 'bean.dailyStreak'
  | 'bean.current'
  | 'bean.best'
  | 'bean.totalXP'
  | 'bean.streakZero'
  | 'bean.last30Days'
  | 'bean.roomies'
  | 'bean.active'
  | 'bean.connectText'
  | 'bean.setupRoomies'
  | 'bean.roomiesSettings'
  | 'bean.preview'
  | 'bean.noActivity'
  | 'bean.keepGoing'
  | 'bean.justNow'
  | 'bean.minsAgo'
  | 'bean.hoursAgo'
  | 'bean.daysAgo'
  | 'bean.levelName.seedling'
  | 'trial.endsToday'
  | 'trial.endsIn'
  | 'trial.upgrade'
  | 'trial.dismiss'
  | 'layout.offlineBanner'
  | 'layout.reconnectSyncing'
  | 'layout.reconnectSynced'
  | 'splash.preparing'
  | 'bean.levelName.sprouting'
  | 'bean.levelName.budding'
  | 'bean.levelName.rooted'
  | 'bean.levelName.thriving'
  | 'bean.levelName.flourishing'
  | 'bean.levelName.resilient'
  | 'bean.levelName.champion'
  | 'bean.levelName.guardian'
  | 'bean.levelName.legend'
  | 'bean.feed.dailyLog'
  | 'bean.feed.dailyLogStreak'
  | 'bean.feed.streakMilestone'
  | 'bean.feed.guideArticle'
  | 'bean.feed.joinedGroup'
  | 'bean.feed.changedLook'
  | 'bean.celebration.levelUpTitle'
  | 'bean.celebration.streakTitle'
  | 'bean.celebration.levelUpBody'
  | 'bean.celebration.streakBody'
  // Notifications
  | 'notifications.medReminder'
  | 'notifications.medReminderBody'
  | 'notifications.dailyReminder'
  | 'notifications.dailyReminderBody'
  | 'notifications.alarmTitle'
  | 'notifications.alarmBody'
  // Guide / Library page
  | 'guide.articles'
  | 'guide.subtitle'
  | 'guide.resources'
  | 'guide.minRead'
  | 'guide.unlocksInDay'
  | 'guide.unlocksInDays'
  | 'guide.category.contacts.title'
  | 'guide.category.contacts.subtitle'
  | 'guide.category.medical_card.title'
  | 'guide.category.medical_card.subtitle'
  | 'guide.category.emergency.title'
  | 'guide.category.emergency.subtitle'
  | 'guide.category.how_to.title'
  | 'guide.category.how_to.subtitle'
  | 'guide.category.hygiene.title'
  | 'guide.category.hygiene.subtitle'
  | 'guide.category.exercise.title'
  | 'guide.category.exercise.subtitle'
  | 'guide.category.wellbeing.title'
  | 'guide.category.wellbeing.subtitle'
  | 'guide.category.food.title'
  | 'guide.category.food.subtitle'
  | 'guide.category.nutrition.title'
  | 'guide.category.nutrition.subtitle'
  | 'guide.category.sleep.title'
  | 'guide.category.sleep.subtitle'
  | 'guide.category.lab_glossary.title'
  | 'guide.category.lab_glossary.subtitle'
  | 'guide.category.vault.title'
  | 'guide.category.vault.subtitle'
  | 'guide.category.reminders.title'
  | 'guide.category.reminders.subtitle'
  | 'guide.category.co-carers.title'
  | 'guide.category.co-carers.subtitle'
  // Auth — Register
  | 'register.disclaimerPrefix'
  | 'register.passwordStrength'
  | 'register.rule.minLength'
  | 'register.rule.uppercase'
  | 'register.rule.lowercase'
  | 'register.rule.number'
  | 'register.rule.special'
  | 'register.strength.veryWeak'
  | 'register.strength.weak'
  | 'register.strength.fair'
  | 'register.strength.strong'
  | 'register.strength.veryStrong'
  | 'register.error.emailInUse'
  | 'register.error.invalidEmail'
  | 'register.error.weakPassword'
  | 'register.error.network'
  | 'register.error.generic'
  | 'register.error.passwordRequirements'
  | 'register.error.passwordsMustMatch'
  | 'register.error.googleFailed'
  // Auth — Login
  | 'login.error.incorrectCredentials'
  | 'login.error.notVerified'
  | 'login.error.tooManyRequests'
  | 'login.error.network'
  | 'login.error.generic'
  | 'login.error.googleFailed'
  // Medical Card
  | 'medcard.title'
  | 'medcard.saveTitleAttr'
  | 'medcard.saved'
  | 'medcard.saving'
  | 'medcard.save'
  | 'medcard.shareAlt'
  | 'medcard.shareFailed'
  | 'medcard.hint'
  | 'medcard.fullName'
  | 'medcard.profession'
  | 'medcard.dob'
  | 'medcard.transplantDate'
  | 'medcard.hospital'
  | 'medcard.processNo'
  | 'medcard.surgeon'
  | 'medcard.allergies'
  | 'medcard.avgWeight'
  | 'medcard.avgBP'
  | 'medcard.medications'
  | 'medcard.placeholder.name'
  | 'medcard.placeholder.profession'
  | 'medcard.placeholder.dob'
  | 'medcard.placeholder.hospital'
  | 'medcard.placeholder.processNo'
  | 'medcard.placeholder.surgeon'
  | 'medcard.placeholder.allergies'
  | 'medcard.diary.title'
  | 'medcard.diary.placeholder'
  | 'medcard.diary.saved'
  | 'medcard.diary.saving'
  // Records — Today's Card
  | 'records.todaysCard'
  | 'records.todaysCardComplete'
  | 'records.allThreeLogged'
  | 'records.filesTooLarge'
  | 'records.stepLocked'
  // Records — new vitals fields
  | 'records.heartRate'
  | 'records.respRate'
  | 'records.heartRateRecorded'
  | 'records.respRateRecorded'
  | 'records.temperatureRecorded'
  | 'records.undoHeartRate'
  | 'records.undoRespRate'
  | 'records.undoTemperature'
  | 'records.wellnessCheck'
  | 'records.mucousMembranes'
  | 'records.mm.pink'
  | 'records.mm.red'
  | 'records.mm.blue'
  | 'records.mm.white'
  | 'records.csv.heartRate'
  | 'records.csv.respRate'
  | 'records.csv.mucousMembrane'
  | 'records.csv.temp'
  | 'records.share.fluidIn'
  | 'records.countFor15s'
  | 'records.countBreathsFor15s'
  | 'records.beats'
  | 'records.breaths'
  | 'records.saveHeartRate'
  | 'records.saveRespRate'
  | 'records.temperature'
  | 'records.saveTemperature'
  // Trends
  | 'trends.showLast'
  // Guide wrappers
  | 'guide.renderError'
  // AppointmentCalendar
  | 'appt.kind.checkup'
  | 'appt.kind.lab'
  | 'appt.kind.medication'
  | 'appt.kind.other'
  | 'appt.noAppointments'
  | 'appt.upcoming'
  | 'appt.today'
  | 'appt.addEvent'
  | 'appt.editEvent'
  | 'appt.titleLabel'
  | 'appt.titlePlaceholder'
  | 'appt.dateLabel'
  | 'appt.timeLabel'
  | 'appt.kindLabel'
  | 'appt.doctorLabel'
  | 'appt.doctorPlaceholder'
  | 'appt.locationLabel'
  | 'appt.locationPlaceholder'
  | 'appt.save'
  | 'appt.cancel'
  | 'appt.delete'
  | 'appt.deleteConfirm'
  // Records share strings
  | 'records.weekOf'
  | 'records.share.title'
  | 'records.share.weight'
  | 'records.share.morning'
  | 'records.share.afternoon'
  | 'records.share.fluidInOut'
  | 'records.share.note'
  | 'records.share.dataTitle'
  | 'records.share.dataText'
  // Notifications accessibility
  | 'notifications.dismiss'
  | 'notifications.bellLabel'
  | 'notifications.settingsLabel'
  | 'notifications.fallbackTitle'
  | 'notifications.fallbackBody'
  | 'fab.voiceHint'
  | 'fab.voicePhrase'
  // Onboarding walkthrough
  | 'walkthrough.polyName'
  | 'walkthrough.skip'
  | 'walkthrough.back'
  | 'walkthrough.letsGo'
  | 'walkthrough.next'
  | 'walkthrough.finish'
  | 'walkthrough.step0.title'
  | 'walkthrough.step0.body'
  | 'walkthrough.step1.title'
  | 'walkthrough.step1.body'
  | 'walkthrough.step2.title'
  | 'walkthrough.step2.body'
  | 'walkthrough.step3.title'
  | 'walkthrough.step3.body'
  | 'walkthrough.step4.title'
  | 'walkthrough.step4.body'
  | 'walkthrough.step5.title'
  | 'walkthrough.step5.body'
  | 'options.replayWalkthrough'
  // Co-carer / pet members
  | 'members.title'
  | 'members.membersTitle'
  | 'members.inviteTitle'
  | 'members.inviteHint'
  | 'members.createLink'
  | 'members.copyLink'
  | 'members.copied'
  | 'members.linkCopied'
  | 'members.inviteError'
  | 'members.pendingTitle'
  | 'members.cancelInvite'
  | 'members.owner'
  | 'members.remove'
  | 'members.confirmRemove'
  | 'members.joinedOn'
  | 'members.sentOn'
  | 'members.loading'
  | 'members.noMembers'
  | 'members.viewOnlyNotice'
  | 'members.coCarers'
  | 'members.coCarersSubtitle'
  // Ownership transfer
  | 'members.transferTitle'
  | 'members.transferHint'
  | 'members.transferWarning'
  | 'members.transferEmail'
  | 'members.transferSelectCarer'
  | 'members.transferAction'
  | 'members.transferConfirm'
  | 'members.transferSuccess'
  | 'members.transferError'
  // Dose claiming
  | 'doses.markAsGiven'
  | 'doses.givenBy'
  | 'doses.at'
  | 'doses.alreadyGiven';

const TRANSLATIONS: Record<SupportedLanguage, Record<TranslationKey, string>> = {
    English: en,
    French: fr,
    German: de,
    Portuguese: pt,
    Spanish: es,
  };
const LANGUAGE_TAGS: Record<SupportedLanguage, string> = {
  English: 'en',
  French: 'fr',
  German: 'de',
  Portuguese: 'pt',
  Spanish: 'es',
};

const DATE_LOCALES: Record<SupportedLanguage, string> = {
  English: 'en-US',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

export function translate(language: SupportedLanguage, key: TranslationKey): string {
  return TRANSLATIONS[language]?.[key] ?? TRANSLATIONS.English[key] ?? key;
}

export function getLanguageTag(language: SupportedLanguage): string {
  return LANGUAGE_TAGS[language] ?? 'en';
}

export function getDateLocale(language: SupportedLanguage): string {
  return DATE_LOCALES[language] ?? 'en-US';
}
